import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Grid,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Refresh, BugReport } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { API_ENDPOINTS } from '../config/api';

interface ApiLog {
  _id: string;
  studioId: string;
  studioName: string;
  method: string;
  endpoint: string;
  statusCode: number;
  responseTimeMs: number;
  userId: string | null;
  ipAddress: string;
  userAgent: string;
  timestamp: string;
}

interface Stats {
  period: { days: number; since: string };
  summary: {
    totalCalls: number;
    avgResponseMs: number;
    errorCount: number;
    uniqueUserCount: number;
  };
  byEndpoint: { method: string; endpoint: string; count: number; avgResponseMs: number; errorCount: number }[];
  byDay: { date: string; count: number; errorCount: number }[];
  byStudio: { studioId: string; studioName: string; count: number }[];
}

const METHOD_COLORS: Record<string, 'success' | 'primary' | 'warning' | 'error' | 'default'> = {
  GET: 'success',
  POST: 'primary',
  PUT: 'warning',
  PATCH: 'warning',
  DELETE: 'error',
};

const statusColor = (code: number): 'success' | 'warning' | 'error' | 'default' => {
  if (code < 300) return 'success';
  if (code < 400) return 'warning';
  return 'error';
};

const PartnerApiLogsPage: React.FC = () => {
  const [logs, setLogs] = useState<ApiLog[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [days, setDays] = useState(7);
  const [filterEndpoint, setFilterEndpoint] = useState('');
  const [filterMethod, setFilterMethod] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res: any = await apiService.get(
        `${API_ENDPOINTS.PARTNER.API_STATS}?days=${days}`,
      );
      setStats(res);
    } catch {
      // stats failure is non-fatal
    } finally {
      setStatsLoading(false);
    }
  }, [days]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        pageSize: String(rowsPerPage),
      });
      if (filterEndpoint) params.set('endpoint', filterEndpoint);
      if (filterMethod) params.set('method', filterMethod);
      if (filterStatus) params.set('statusCode', filterStatus);

      const res: any = await apiService.get(
        `${API_ENDPOINTS.PARTNER.API_LOGS}?${params.toString()}`,
      );
      setLogs(res.logs || []);
      setTotal(res.total || 0);
    } catch (e: any) {
      setError(e?.message || 'Failed to fetch logs');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filterEndpoint, filterMethod, filterStatus]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleRefresh = () => {
    fetchStats();
    fetchLogs();
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false });
  };

  const truncate = (s: string, n = 40) => (s.length > n ? s.slice(0, n) + '…' : s);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <BugReport sx={{ color: 'text.secondary', fontSize: 28 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>Partner API Logs</Typography>
          <Typography variant="body2" color="text.secondary">
            Debug view — tracks all calls to <code>/api/v1/partner/*</code>
          </Typography>
        </Box>
        <Chip label="Super Admin Only" color="warning" size="small" variant="outlined" />
        <IconButton onClick={handleRefresh} title="Refresh">
          <Refresh />
        </IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Period selector */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">Stats period:</Typography>
        {[1, 7, 14, 30].map(d => (
          <Chip
            key={d}
            label={`${d}d`}
            size="small"
            variant={days === d ? 'filled' : 'outlined'}
            color={days === d ? 'primary' : 'default'}
            onClick={() => setDays(d)}
            sx={{ cursor: 'pointer' }}
          />
        ))}
      </Box>

      {/* Stats cards */}
      {statsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : stats ? (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" fontWeight={700}>{stats.summary.totalCalls.toLocaleString()}</Typography>
                <Typography variant="body2" color="text.secondary">Total Calls</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" fontWeight={700} color="success.main">
                  {stats.summary.uniqueUserCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">Unique Users</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" fontWeight={700} color={stats.summary.errorCount > 0 ? 'error.main' : 'text.primary'}>
                  {stats.summary.errorCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">Errors (4xx/5xx)</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" fontWeight={700}>{stats.summary.avgResponseMs}ms</Typography>
                <Typography variant="body2" color="text.secondary">Avg Response</Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Daily trend */}
          {stats.byDay.length > 0 && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>Calls per Day</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    {stats.byDay.map(d => (
                      <Box key={d.date} sx={{ textAlign: 'center', minWidth: 48 }}>
                        <Box
                          sx={{
                            height: 40,
                            width: 40,
                            mx: 'auto',
                            bgcolor: d.errorCount > 0 ? 'error.light' : 'primary.light',
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography variant="caption" fontWeight={700}>{d.count}</Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                          {d.date.slice(5)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Endpoint breakdown */}
          {stats.byEndpoint.length > 0 && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>Top Endpoints</Typography>
                  <Table size="small">
                    <TableBody>
                      {stats.byEndpoint.slice(0, 8).map((e, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ py: 0.5, px: 1 }}>
                            <Chip label={e.method} color={METHOD_COLORS[e.method] || 'default'} size="small" />
                          </TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: 12, fontFamily: 'monospace' }}>
                            {e.endpoint.replace('/api/v1/partner', '')}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.5 }}>
                            <Typography variant="caption" fontWeight={600}>{e.count}</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">{e.avgResponseMs}ms</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* By studio */}
          {stats.byStudio.length > 0 && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>By Studio</Typography>
                  <Table size="small">
                    <TableBody>
                      {stats.byStudio.map((s, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ py: 0.5 }}>{s.studioName || s.studioId}</TableCell>
                          <TableCell align="right" sx={{ py: 0.5 }}>
                            <Chip label={s.count} size="small" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      ) : null}

      {/* Filters */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              label="Endpoint filter"
              size="small"
              value={filterEndpoint}
              onChange={e => { setFilterEndpoint(e.target.value); setPage(0); }}
              placeholder="e.g. sessions"
              sx={{ width: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Method</InputLabel>
              <Select
                value={filterMethod}
                label="Method"
                onChange={e => { setFilterMethod(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="GET">GET</MenuItem>
                <MenuItem value="POST">POST</MenuItem>
                <MenuItem value="PUT">PUT</MenuItem>
                <MenuItem value="DELETE">DELETE</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={e => { setFilterStatus(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="200">200 OK</MenuItem>
                <MenuItem value="201">201 Created</MenuItem>
                <MenuItem value="400">400 Bad Request</MenuItem>
                <MenuItem value="401">401 Unauthorized</MenuItem>
                <MenuItem value="404">404 Not Found</MenuItem>
                <MenuItem value="500">500 Server Error</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
              {total.toLocaleString()} records
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Logs table */}
      <Card variant="outlined">
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Time (IST)</TableCell>
                    <TableCell>Studio</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell>Endpoint</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="right">Response</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>IP</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No logs found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map(log => (
                      <TableRow
                        key={log._id}
                        sx={{ '&:hover': { bgcolor: 'action.hover' }, bgcolor: log.statusCode >= 400 ? 'error.50' : 'inherit' }}
                      >
                        <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                          {formatTime(log.timestamp)}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{log.studioName || '—'}</TableCell>
                        <TableCell>
                          <Chip label={log.method} color={METHOD_COLORS[log.method] || 'default'} size="small" />
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                          <Tooltip title={log.endpoint} placement="top">
                            <span>{truncate(log.endpoint.replace('/api/v1/partner', ''), 35)}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={log.statusCode} color={statusColor(log.statusCode)} size="small" />
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>
                          <Typography
                            variant="caption"
                            color={log.responseTimeMs > 2000 ? 'error' : log.responseTimeMs > 500 ? 'warning.main' : 'text.secondary'}
                          >
                            {log.responseTimeMs}ms
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: 11, fontFamily: 'monospace' }}>
                          {log.userId ? truncate(String(log.userId), 12) : '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: 11 }}>{log.ipAddress || '—'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[50]}
            />
          </>
        )}
      </Card>
    </Box>
  );
};

export default PartnerApiLogsPage;
